/**
 * `/news` and `/news/:slug` — the only two real pages the site has besides the
 * landing page itself.
 *
 * Janeks: *"The only new pages are - News, inside it - news articles."*
 * Everything else in the menu is an anchor down the landing page, and the
 * policies are pages of their own (`legal-view.ts`).
 *
 * ─── Same chrome, deliberately ──────────────────────────────────────────────
 * The header, drawer and footer come from `landing-chrome.ts` so there is one
 * definition of the menu rather than three that drift. The section items in
 * that menu become `/#id` here instead of `#id` — a bare fragment on this page
 * scrolls to nothing.
 *
 * ─── The content never becomes HTML ─────────────────────────────────────────
 * `News.content` is the same kind of column as `LegalPage.content`: untyped,
 * admin-editable from `/admin`, never sanitised by core. It is rendered
 * through `markdown-lite.ts`, which builds DOM nodes and writes author text
 * with `textContent`. The SUMMARY and TITLE are escaped for the same reason.
 */
import { injectLandingStyles } from './landing-styles';
import { footerMarkup, headerMarkup, wireChrome } from './landing-chrome';
import { loadNews, loadNewsArticle } from './landing-cms';
import { renderMarkdown } from './markdown-lite';
import { newsCardMarkup, safeImageUrl, shortDate } from './news-card';
import { escapeAttr } from './landing-escape';
import { BRAND } from './landing-copy';

export interface NewsViewOptions {
  navigate: (to: string) => void;
}

/** Wraps a page body in the shared chrome and wires it. Returns the teardown. */
export function mountPage(
  root: HTMLElement,
  opts: NewsViewOptions,
  body: string,
): { page: HTMLElement; cleanups: Array<() => void> } {
  const removeStyles = injectLandingStyles();
  root.innerHTML = `
<div class="lp lp-subpage">
  <div class="lp-grain"></div>
  ${headerMarkup(false)}
  <main>${body}</main>
  ${footerMarkup(false)}
</div>`;
  const page = root.querySelector('.lp') as HTMLElement;
  const cleanups: Array<() => void> = [
    removeStyles,
    wireChrome(page, { onLanding: false, navigate: opts.navigate }),
  ];
  return { page, cleanups };
}

// ─── /news ───────────────────────────────────────────────────────────────────

export function renderNewsIndex(root: HTMLElement, opts: NewsViewOptions): () => void {
  const { page, cleanups } = mountPage(root, opts, `
    <section class="lp-section lp-news">
      <p class="lp-eyebrow">Dispatches</p>
      <h1 class="lp-h2">Word from New Ashford</h1>
      <p class="lp-body">
        What the settlement is talking about — patch notes, what the scouts brought back,
        and the occasional notice nailed to the board by the gate.
      </p>
      <div class="lp-news-list" data-news><p class="lp-news-empty">Reading the board…</p></div>
    </section>`);

  document.title = `News — ${BRAND.name}`;
  const host = page.querySelector<HTMLElement>('[data-news]')!;
  let cancelled = false;

  void loadNews().then((result) => {
    if (cancelled) return;
    if (result.items.length === 0) {
      // Not an error: a deployment that has published nothing, and a preview
      // build (which empties this endpoint by design), both land here.
      host.innerHTML = '<p class="lp-news-empty">Nothing on the board yet. Check back.</p>';
      return;
    }
    host.innerHTML = result.items.map(newsCardMarkup).join('');
  });

  // Delegated so the cards can be replaced wholesale when the fetch lands.
  //
  // `stopPropagation` was missing here, which meant `main.ts`'s document-level
  // handler navigated to the same article on the same click — two identical
  // history entries, so Back took two presses to return to the board. See the
  // note at `onPlay` in `landing-chrome.ts`.
  const onClick = (e: Event): void => {
    const a = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[data-news-slug]');
    if (!a) return;
    e.preventDefault();
    e.stopPropagation();
    opts.navigate(a.getAttribute('href')!);
  };
  host.addEventListener('click', onClick);
  cleanups.push(() => host.removeEventListener('click', onClick), () => { cancelled = true; });

  return () => {
    for (const fn of cleanups) fn();
    root.innerHTML = '';
  };
}

// ─── /news/:slug ─────────────────────────────────────────────────────────────

export function renderNewsArticle(
  root: HTMLElement,
  slug: string,
  opts: NewsViewOptions,
): () => void {
  // Four slots in the order Janeks asked for — *"Publish date should be
  // output, title, article intro, article body"* — plus the header image
  // between the intro and the body.
  //
  // The intro and the figure start HIDDEN rather than empty. Both are
  // optional on a row (`summary` can be blank, `imageUrl` is nullable) and
  // both carry their own spacing, so an empty one left in flow opens a gap
  // under the headline that reads as a failed load. The error path below
  // leaves them hidden for the same reason.
  const { page, cleanups } = mountPage(root, opts, `
    <article class="lp-section lp-article">
      <a class="lp-back" href="/news">← All dispatches</a>
      <p class="lp-eyebrow" data-meta>Dispatch</p>
      <h1 class="lp-h2" data-title>Loading…</h1>
      <p class="lp-article-intro" data-intro hidden></p>
      <figure class="lp-article-figure" data-figure hidden></figure>
      <div class="lp-article-body" data-body></div>
    </article>`);

  const metaEl = page.querySelector<HTMLElement>('[data-meta]')!;
  const titleEl = page.querySelector<HTMLElement>('[data-title]')!;
  const introEl = page.querySelector<HTMLElement>('[data-intro]')!;
  const figureEl = page.querySelector<HTMLElement>('[data-figure]')!;
  const bodyEl = page.querySelector<HTMLElement>('[data-body]')!;
  let cancelled = false;

  const onBack = (e: Event): void => {
    const a = (e.target as HTMLElement).closest<HTMLAnchorElement>('.lp-back');
    if (!a) return;
    e.preventDefault();
    opts.navigate('/news');
  };
  page.addEventListener('click', onBack);
  cleanups.push(() => page.removeEventListener('click', onBack), () => { cancelled = true; });

  void loadNewsArticle(slug)
    .then((doc) => {
      if (cancelled) return;
      titleEl.textContent = doc.title;
      metaEl.textContent = [doc.tag, shortDate(doc.publishedAt)].filter(Boolean).join(' · ');
      document.title = `${doc.title} — ${BRAND.name}`;

      // `textContent`, not markup: the summary is admin-editable and core
      // never sanitises it, exactly like `content`.
      if (doc.summary) {
        introEl.textContent = doc.summary;
        introEl.hidden = false;
      }

      const image = safeImageUrl(doc.imageUrl);
      if (image) {
        // `alt=""` — the picture illustrates the headline directly above it
        // and says nothing the article does not. Not lazy, unlike the cards:
        // this one is the thing the reader came for and is above the fold.
        figureEl.innerHTML = `<img src="${escapeAttr(image)}" alt="" decoding="async">`;
        figureEl.hidden = false;
      }

      renderMarkdown(bodyEl, doc.content);
    })
    .catch(() => {
      if (cancelled) return;
      titleEl.textContent = 'Not found';
      metaEl.textContent = 'Dispatch';
      // `GET /cms/news/:slug` answers 404 for an unpublished article and for a
      // slug that never existed alike, so say what is knowable and no more.
      bodyEl.className = 'lp-article-body lp-sheet-error';
      bodyEl.textContent = 'That dispatch is not on the board. It may have been taken down, '
        + 'or the link may be wrong.';
    });

  return () => {
    for (const fn of cleanups) fn();
    root.innerHTML = '';
  };
}
